import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type NomenclatureKind } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Управління видами номенклатури (tenant-scoped, 2026-06-06).
 * Дефолтні 9 видів (is_seed) — не можна видалити. Юзер створює свої.
 * Тип (is_service) виду визначає тип номенклатури у її картці.
 */
export default function NomenclatureKindsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newIsService, setNewIsService] = useState(false);

  const list = useQuery({
    queryKey: ['nomenclature-kinds'],
    queryFn: refs.nomenclatureKinds.list,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['nomenclature-kinds'] });

  const create = useMutation({
    mutationFn: () =>
      refs.nomenclatureKinds.create({ name: newName.trim(), is_service: newIsService }),
    onSuccess: () => { setNewName(''); setNewIsService(false); invalidate(); },
  });
  const update = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<NomenclatureKind> }) =>
      refs.nomenclatureKinds.update(id, { name: p.name, is_service: p.is_service }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.nomenclatureKinds.remove(id),
    onSuccess: invalidate,
  });

  const rows = list.data ?? [];

  const cols = useMemo<ColumnDef<NomenclatureKind>[]>(() => [
    {
      key: 'name', header: 'Назва',
      sortValue: (k) => k.name.toLowerCase(),
      cell: (k) => (
        <>
          {k.is_seed && <span title="Дефолтний вид">🔒 </span>}
          {k.is_seed ? (
            k.name
          ) : (
            <input
              defaultValue={k.name}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== k.name) update.mutate({ id: k.id, p: { name: v } });
              }}
              style={{ width: '90%' }}
            />
          )}
        </>
      ),
    },
    {
      key: 'is_service', header: 'Тип', width: 120, align: 'center',
      sortValue: (k) => (k.is_service ? 1 : 0),
      cell: (k) => (k.is_service ? 'Послуга' : 'Товар'),
    },
    {
      key: 'actions', header: '', width: 100, align: 'center',
      cell: (k) => (
        !k.is_seed && (
          <button
            className="danger"
            title="Видалити вид"
            onClick={() =>
              confirm(`Видалити вид «${k.name}»?`).then((ok) => {
                if (ok) remove.mutate(k.id);
              })
            }
          >
            ✕
          </button>
        )
      ),
    },
  ], [update, remove, confirm]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Види номенклатури</h2>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Вид номенклатури визначає її <strong>тип</strong> (товар або послуга).
        Дефолтні види позначені 🔒 — їх не можна видалити. Створюйте власні
        види за потреби.
      </p>

      {/* Створення нового виду */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '1rem 0' }}>
        <input
          placeholder="Назва нового виду"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={newIsService}
            onChange={(e) => setNewIsService(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Послуга
        </label>
        <button
          className="primary"
          disabled={!newName.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          + Додати вид
        </button>
      </div>

      <div style={{ maxWidth: 720 }}>
        <DataTable<NomenclatureKind>
          rows={rows}
          columns={cols}
          rowKey={(k) => k.id}
          loading={list.isLoading}
          emptyText="Немає видів номенклатури."
          rowMuted={(k) => (k.deletion_mark ? 0.4 : undefined)}
          initialSortKey="name"
        />
      </div>
    </>
  );
}
